import { createSlice } from '@reduxjs/toolkit';
import { loginThunk, registerThunk, refreshTokenThunk, logoutThunk, logoutAllThunk, checkAuthThunk, createAccountThunk, updateProfileThunk, changePasswordThunk, checkApiStatusThunk } from './authThunks';
// ===== INITIAL STATE =====
const initialState = {
    // User data
    user: null,
    account: null,
    // Tokens
    accessToken: null,
    refreshToken: null,
    // JWT payload
    jwtPayload: null,
    // Loading states
    isLoading: false,
    isAuthenticated: false,
    isInitialized: false,
    // API status
    isApiConnected: false,
    apiStatus: 'checking',
    // Error handling
    error: null,
    lastError: null,
};
// ===== AUTH SLICE =====
export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        // Clear error
        clearError: (state) => {
            state.error = null;
        },
        // Clear last error
        clearLastError: (state) => {
            state.lastError = null;
        },
        // Set loading state
        setLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        // Set API status
        setApiStatus: (state, action) => {
            state.isApiConnected = action.payload.isConnected;
            state.apiStatus = action.payload.status;
        },
        // Update tokens only (for refresh)
        updateTokens: (state, action) => {
            state.accessToken = action.payload.accessToken;
            if (action.payload.refreshToken) {
                state.refreshToken = action.payload.refreshToken;
            }
            state.jwtPayload = action.payload.jwtPayload;
            // Update localStorage
            localStorage.setItem('auth_token', action.payload.accessToken);
            if (action.payload.refreshToken) {
                localStorage.setItem('refresh_token', action.payload.refreshToken);
            }
        },
        // Clear auth data
        clearAuth: (state) => {
            state.user = null;
            state.account = null;
            state.accessToken = null;
            state.refreshToken = null;
            state.jwtPayload = null;
            state.isAuthenticated = false;
            state.error = null;
            // Clear localStorage
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
        },
        // Set initialized
        setInitialized: (state) => {
            state.isInitialized = true;
        },
    },
    extraReducers: (builder) => {
        // ===== LOGIN =====
        builder
            .addCase(loginThunk.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        })
            .addCase(loginThunk.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isAuthenticated = true;
            state.user = action.payload.user;
            state.accessToken = action.payload.tokens.access_token;
            state.refreshToken = action.payload.tokens.refresh_token;
            state.jwtPayload = action.payload.jwtPayload;
            state.error = null;
            // Save to localStorage
            localStorage.setItem('auth_token', action.payload.tokens.access_token);
            localStorage.setItem('refresh_token', action.payload.tokens.refresh_token);
            localStorage.setItem('user', JSON.stringify(action.payload.user));
        })
            .addCase(loginThunk.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
            state.lastError = action.payload;
        });
        // ===== REGISTER =====
        builder
            .addCase(registerThunk.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        })
            .addCase(registerThunk.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isAuthenticated = true;
            state.user = action.payload.user;
            state.accessToken = action.payload.tokens.access_token;
            state.refreshToken = action.payload.tokens.refresh_token;
            state.error = null;
            // Save to localStorage
            localStorage.setItem('auth_token', action.payload.tokens.access_token);
            localStorage.setItem('refresh_token', action.payload.tokens.refresh_token);
            localStorage.setItem('user', JSON.stringify(action.payload.user));
        })
            .addCase(registerThunk.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
            state.lastError = action.payload;
        });
        // ===== REFRESH TOKEN =====
        builder
            .addCase(refreshTokenThunk.pending, (state) => {
            // Don't set loading for refresh to avoid UI flicker
        })
            .addCase(refreshTokenThunk.fulfilled, (state, action) => {
            state.accessToken = action.payload.access_token;
            if (action.payload.refresh_token) {
                state.refreshToken = action.payload.refresh_token;
            }
            state.jwtPayload = action.payload.jwtPayload;
            // Update localStorage
            localStorage.setItem('auth_token', action.payload.access_token);
            if (action.payload.refresh_token) {
                localStorage.setItem('refresh_token', action.payload.refresh_token);
            }
        })
            .addCase(refreshTokenThunk.rejected, (state, action) => {
            // If refresh fails, logout user
            state.user = null;
            state.account = null;
            state.accessToken = null;
            state.refreshToken = null;
            state.jwtPayload = null;
            state.isAuthenticated = false;
            state.error = action.payload;
            // Clear localStorage
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
        });
        // ===== LOGOUT =====
        builder
            .addCase(logoutThunk.pending, (state) => {
            state.isLoading = true;
        })
            .addCase(logoutThunk.fulfilled, (state) => {
            state.user = null;
            state.account = null;
            state.accessToken = null;
            state.refreshToken = null;
            state.jwtPayload = null;
            state.isAuthenticated = false;
            state.isLoading = false;
            state.error = null;
            // Clear localStorage
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
        })
            .addCase(logoutThunk.rejected, (state) => {
            // Even if logout fails, clear local state
            state.user = null;
            state.account = null;
            state.accessToken = null;
            state.refreshToken = null;
            state.jwtPayload = null;
            state.isAuthenticated = false;
            state.isLoading = false;
            // Clear localStorage
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
        });
        // ===== LOGOUT ALL =====
        builder
            .addCase(logoutAllThunk.pending, (state) => {
            state.isLoading = true;
        })
            .addCase(logoutAllThunk.fulfilled, (state) => {
            state.user = null;
            state.account = null;
            state.accessToken = null;
            state.refreshToken = null;
            state.jwtPayload = null;
            state.isAuthenticated = false;
            state.isLoading = false;
            state.error = null;
            // Clear localStorage
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
        })
            .addCase(logoutAllThunk.rejected, (state) => {
            // Even if logout fails, clear local state
            state.user = null;
            state.account = null;
            state.accessToken = null;
            state.refreshToken = null;
            state.jwtPayload = null;
            state.isAuthenticated = false;
            state.isLoading = false;
            // Clear localStorage
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
        });
        // ===== CHECK AUTH =====
        builder
            .addCase(checkAuthThunk.pending, (state) => {
            state.isLoading = true;
        })
            .addCase(checkAuthThunk.fulfilled, (state, action) => {
            if (action.payload) {
                state.user = action.payload.user;
                state.accessToken = action.payload.tokens.access_token;
                state.refreshToken = action.payload.tokens.refresh_token;
                state.jwtPayload = action.payload.jwtPayload;
                state.isAuthenticated = true;
            }
            state.isLoading = false;
            state.isInitialized = true;
        })
            .addCase(checkAuthThunk.rejected, (state) => {
            state.isLoading = false;
            state.isInitialized = true;
            state.isAuthenticated = false;
            // Clear any stale data
            state.user = null;
            state.account = null;
            state.accessToken = null;
            state.refreshToken = null;
            state.jwtPayload = null;
            // Clear localStorage
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
        });
        // ===== CREATE ACCOUNT =====
        builder
            .addCase(createAccountThunk.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        })
            .addCase(createAccountThunk.fulfilled, (state, action) => {
            state.isLoading = false;
            state.account = action.payload.account;
            if (action.payload.tokens) {
                state.user = action.payload.owner;
                state.accessToken = action.payload.tokens.access_token;
                state.refreshToken = action.payload.tokens.refresh_token;
                state.isAuthenticated = true;
                // Save to localStorage
                localStorage.setItem('auth_token', action.payload.tokens.access_token);
                localStorage.setItem('refresh_token', action.payload.tokens.refresh_token);
                localStorage.setItem('user', JSON.stringify(action.payload.owner));
            }
        })
            .addCase(createAccountThunk.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
            state.lastError = action.payload;
        });
        // ===== UPDATE PROFILE =====
        builder
            .addCase(updateProfileThunk.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        })
            .addCase(updateProfileThunk.fulfilled, (state, action) => {
            state.isLoading = false;
            state.user = action.payload;
            // Update localStorage
            localStorage.setItem('user', JSON.stringify(action.payload));
        })
            .addCase(updateProfileThunk.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
            state.lastError = action.payload;
        });
        // ===== CHANGE PASSWORD =====
        builder
            .addCase(changePasswordThunk.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        })
            .addCase(changePasswordThunk.fulfilled, (state) => {
            state.isLoading = false;
        })
            .addCase(changePasswordThunk.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
            state.lastError = action.payload;
        });
        // ===== CHECK API STATUS =====
        builder
            .addCase(checkApiStatusThunk.pending, (state) => {
            state.apiStatus = 'checking';
        })
            .addCase(checkApiStatusThunk.fulfilled, (state, action) => {
            state.isApiConnected = action.payload;
            state.apiStatus = action.payload ? 'connected' : 'mock';
        })
            .addCase(checkApiStatusThunk.rejected, (state) => {
            state.isApiConnected = false;
            state.apiStatus = 'mock';
        });
    },
});
// ===== ACTIONS =====
export const { clearError, clearLastError, setLoading, setApiStatus, updateTokens, clearAuth, setInitialized, } = authSlice.actions;
// ===== REDUCER =====
export default authSlice.reducer;
